import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { People, Schedule, ArrowForward } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { getPetitions } from '../lib/supabase';

const HomePage = () => {
  const [petitions, setPetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPetitions = async () => {
      try {
        const data = await getPetitions();
        setPetitions(data);
      } catch (err) {
        setError('Eroare la încărcarea petițiilor');
        console.error('Error fetching petitions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPetitions();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #004b96 0%, #1976d2 100%)',
          color: 'white',
          py: 8,
          px: 4,
          borderRadius: 4,
          mb: 8,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h1" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3rem' },
              mb: 2,
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              lineHeight: 1.2,
              color: 'white'
            }}
          >
            Partidul Social Democrat European
          </Typography>
          
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3, 
              fontWeight: 300,
              fontSize: { xs: '1.2rem', md: '1.5rem' },
              color: 'white'
            }}
          >
            Platforma de Petiții
          </Typography>
          
          <Box 
            sx={{ 
              width: 80, 
              height: 4, 
              bgcolor: 'secondary.main', 
              mx: 'auto', 
              mb: 4, 
              borderRadius: 2 
            }} 
          />
          
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 4,
              fontWeight: 400,
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              lineHeight: 1.4,
              color: 'white'
            }}
          >
            Vocea ta contează în construirea unei Europe sociale și democratice
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              maxWidth: 700, 
              mx: 'auto',
              fontSize: '1.1rem',
              lineHeight: 1.7,
              opacity: 0.85
            }}
          >
            Platforma digitală a Partidului Social Democrat European pentru petițiile cetățenilor. 
            Aici poți susține cauzele importante și poți contribui la schimbările de care Europa are nevoie.
          </Typography>
        </Container>
      </Box>

      {/* Petitions Grid */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom textAlign="center" sx={{ mb: 4 }}>
          Petițiile Active
        </Typography>
        {petitions.length === 0 ? (
          <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto' }}>
            Nu există petițiii active în acest moment. Revino mai târziu!
          </Alert>
        ) : (
          <Grid container spacing={4}>
            {petitions.map((petition) => (
              <Grid item xs={12} md={6} lg={6} key={petition.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 30px rgba(0, 75, 150, 0.15)',
                      borderColor: 'primary.main',
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {petition.title}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 3,
                        lineHeight: 1.6,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {petition.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.main', color: 'white', px: 2, py: 0.5, borderRadius: 2 }}>
                        <People fontSize="small" />
                        <Typography variant="body2" fontWeight={600}>
                          {petition.signatures?.[0]?.count || 0} semnături
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Schedule fontSize="small" color="text.secondary" />
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(petition.created_at), 'dd MMM yyyy', { locale: ro })}
                        </Typography>
                      </Box>
                    </Box>

                    {petition.target_signatures && (
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Progres către obiectiv
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="primary.main">
                            {Math.round(((petition.signatures?.[0]?.count || 0) / petition.target_signatures) * 100)}%
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            width: '100%', 
                            height: 10, 
                            bgcolor: 'grey.200', 
                            borderRadius: 5,
                            overflow: 'hidden'
                          }}
                        >
                          <Box 
                            sx={{ 
                              width: `${Math.min(100, ((petition.signatures?.[0]?.count || 0) / petition.target_signatures) * 100)}%`,
                              height: '100%',
                              bgcolor: 'primary.main',
                              transition: 'width 0.5s ease',
                              borderRadius: 5
                            }} 
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Obiectiv: {petition.target_signatures.toLocaleString()} semnături
                        </Typography>
                      </Box>
                    )}

                    {petition.category && (
                      <Chip 
                        label={petition.category} 
                        size="small" 
                        sx={{ 
                          bgcolor: 'secondary.light', 
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    )}
                  </CardContent>
                  
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      component={Link}
                      to={`/petitie/${petition.id}`}
                      variant="contained"
                      endIcon={<ArrowForward />}
                      fullWidth
                      size="large"
                      sx={{ 
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600
                      }}
                    >
                      Vezi & Semnează
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* About Section */}
      <Box sx={{ mt: 8, textAlign: 'center', bgcolor: 'grey.50', p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h2" gutterBottom>
          De ce să semnezi?
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" color="primary" gutterBottom>
              Vocea ta contează
            </Typography>
            <Typography variant="body2">
              Fiecare semnătură este un pas către schimbarea pe care o dorești să o vezi în Europa.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" color="primary" gutterBottom>
              Transparență totală
            </Typography>
            <Typography variant="body2">
              Toate petițiile sunt publice și progresul lor poate fi urmărit în timp real.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" color="primary" gutterBottom>
              Impact real
            </Typography>
            <Typography variant="body2">
              Petițiile cu succes sunt prezentate în fața autorităților competente pentru acțiune.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default HomePage;
